import { LeagueTeam, LeagueMember, MemberStats } from "../types/game";
import { MATCHES_PER_WEEK, LEAGUE_WEEK_RESET_HOUR } from "../constants/league";

const BOT_TEAM_NAMES = [
  "FC Atlas",
  "Dynamo United",
  "Red Storm FC",
  "Blue Star FC",
  "Iron Kings",
  "Solar FC",
  "Storm Riders",
  "Golden Lions",
  "Iron Wolves",
  "Night Hawks",
];

const BOT_F = [
  "Sergio", "Lucas", "Karim", "Antoine", "Rayan",
  "Théo",  "Axel",  "Noah",  "Enzo",   "Kylian",
];
const BOT_L = [
  "Dupont",    "Silva",    "García",    "Müller",    "Kim",
  "Park",      "Benzema",  "Kanté",     "Salah",     "Gnabry",
];

/* Profil 4-stats synthétique d'un bot : sa puissance est répartie selon
   l'archétype de son équipe (seed) + une variation par joueur. La somme des 4
   stats reste égale à la puissance → cohérent avec le modèle agrégé. */
function botProfile(power: number, seed: number, i: number): MemberStats {
  const arch = seed % 3; // 0 = équilibré, 1 = offensif, 2 = défensif
  const w = { technique: 1, defense: 1, physique: 1, vitesse: 1 };
  if (arch === 1) {
    w.technique = 1.4;
    w.physique = 1.2;
    w.defense = 0.7;
  } else if (arch === 2) {
    w.defense = 1.5;
    w.physique = 1.1;
    w.technique = 0.7;
  }
  // Variation individuelle sur la vitesse (profondeur/sprint).
  w.vitesse *= 0.8 + ((seed * (i + 1) * 13) % 7) / 10;
  const sum = w.technique + w.defense + w.physique + w.vitesse || 1;
  return {
    technique: (power * w.technique) / sum,
    defense: (power * w.defense) / sum,
    physique: (power * w.physique) / sum,
    vitesse: (power * w.vitesse) / sum,
  };
}

export function generateBotTeam(division: number, seed: number): LeagueTeam {
  const basePower = 20 + division * 8 + (seed % 5) * 4;
  const members: LeagueMember[] = Array.from({ length: 10 }, (_, i) => {
    const power = basePower + Math.floor((seed * (i + 1) * 7) % 15);
    return {
      name: `${BOT_F[i % BOT_F.length]} ${BOT_L[(i + seed) % BOT_L.length]}`,
      power,
      stats: botProfile(power, seed, i),
      isMe: false,
      isBot: true,
    };
  });

  return {
    id: `bot_${seed}`,
    name: BOT_TEAM_NAMES[seed % BOT_TEAM_NAMES.length],
    members,
    goalkeeperLevel: 1 + Math.floor(division / 2),
    points: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    isBot: true,
  };
}

export function getStandings(teams: LeagueTeam[]): LeagueTeam[] {
  return [...teams].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const gdA = a.goalsFor - a.goalsAgainst;
    const gdB = b.goalsFor - b.goalsAgainst;
    if (gdB !== gdA) return gdB - gdA;
    return b.goalsFor - a.goalsFor;
  });
}

export function applyMatchResult(
  team: LeagueTeam,
  scored: number,
  conceded: number
): LeagueTeam {
  const pts = scored > conceded ? 3 : scored === conceded ? 1 : 0;
  return {
    ...team,
    points: team.points + pts,
    goalsFor: team.goalsFor + scored,
    goalsAgainst: team.goalsAgainst + conceded,
  };
}

export function getPromotionRelegation(
  standings: LeagueTeam[],
  division: number,
  maxDivision: number,
  promoteCount = 1,
  relegateCount = 1
): { promoted: string[]; relegated: string[] } {
  return {
    promoted:
      division < maxDivision
        ? standings.slice(0, promoteCount).map((t) => t.id)
        : [],
    relegated:
      division > 1
        ? standings.slice(-relegateCount).map((t) => t.id)
        : [],
  };
}

export interface DaySchedule {
  day: number;
  scheduledAt: string;
  opponentSeed: number;
}

export function generateWeekSchedule(
  division: number,
  weekStart: string
): DaySchedule[] {
  // Base d'adversaires CONSTANTE sur la semaine. Le consommateur ajoute `day`
  // (0..6) → 7 seeds consécutifs → 7 équipes distinctes (pas de doublon, car
  // 7 entiers consécutifs sont distincts modulo 10 noms). La base varie chaque
  // semaine pour renouveler les adversaires.
  const weekIndex = Math.floor(new Date(weekStart).getTime() / 604800000);
  const weekBase = division * 13 + weekIndex;
  return Array.from({ length: 7 }, (_, day) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + day);
    d.setHours(16, 0, 0, 0);
    return {
      day,
      scheduledAt: d.toISOString(),
      opponentSeed: weekBase,
    };
  });
}

export function isMatchDue(scheduledAt: string): boolean {
  return Date.now() >= new Date(scheduledAt).getTime();
}

export function getCountdownLabel(scheduledAt: string): string {
  const diff = new Date(scheduledAt).getTime() - Date.now();
  if (diff <= 0) return "MATCH EN COURS ⚽";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

/* ─── Résultat déterministe d'une fixture du calendrier ─────────────────────
   Le calendrier hebdo expose 1 match par jour. Pour afficher un bilan stable
   (W/D/L) sans dépendre du journal global, on dérive un résultat déterministe
   à partir du rapport de puissance + une variance pseudo-aléatoire ancrée sur
   le seed de la fixture (mêmes entrées → même résultat à chaque rendu). */
export interface FixtureOutcome {
  result: "win" | "draw" | "loss";
  homeScore: number;
  awayScore: number;
  points: number;
}

export function simulateFixtureOutcome(
  teamPower: number,
  oppPower: number,
  seed: number
): FixtureOutcome {
  let s = (Math.abs(Math.trunc(seed)) % 2147483647) + 1;
  const rng = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };

  const strength = teamPower / (teamPower + oppPower || 1);
  const x = rng();
  const result: "win" | "draw" | "loss" =
    x < strength - 0.12 ? "win" : x > strength + 0.12 ? "loss" : "draw";

  let homeScore: number;
  let awayScore: number;
  if (result === "win") {
    homeScore = 1 + Math.floor(rng() * 3);
    awayScore = Math.max(0, homeScore - 1 - Math.floor(rng() * homeScore));
  } else if (result === "loss") {
    awayScore = 1 + Math.floor(rng() * 3);
    homeScore = Math.max(0, awayScore - 1 - Math.floor(rng() * awayScore));
  } else {
    homeScore = Math.floor(rng() * 3);
    awayScore = homeScore;
  }

  return {
    result,
    homeScore,
    awayScore,
    points: result === "win" ? 3 : result === "draw" ? 1 : 0,
  };
}

/* ─── Mini-ligue hebdomadaire cohérente (round-robin déterministe) ──────────
   La poule = 8 équipes : moi + mes 7 adversaires de la semaine. On joue un
   round-robin complet (chaque équipe affronte les 7 autres, 1 match/jour sur
   7 jours), avec la MÊME règle de points pour tout le monde (V×3 + N×1). Tout
   est déterministe (semaine + seeds + puissances) → chaque joueur voit
   exactement le même classement, et chaque équipe a un vrai bilan W/D/L.
   Mon calendrier affiché (jour d → adversaire seed weekBase+d) reste la même
   source : les seeds des bots sont assignés pour coïncider avec lui. */
export interface TeamFixture {
  round: number; // 0..6 (= jour de la semaine)
  scheduledAt: string;
  opponentName: string;
  opponentSeed: number | null; // null = moi
  opponentIsMe: boolean;
  result: "win" | "draw" | "loss" | null; // null = pas encore joué
  goalsFor: number | null;
  goalsAgainst: number | null;
  due: boolean;
}

export interface TeamStanding {
  name: string;
  seed: number | null; // null = mon équipe
  power: number;
  isMe: boolean;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  fixtures: TeamFixture[];
}

export interface WeekTable {
  standings: TeamStanding[];
  myRank: number;
  matchesDone: number; // matchs disputés par MON équipe
}

function mirrorResult(r: "win" | "draw" | "loss"): "win" | "draw" | "loss" {
  return r === "win" ? "loss" : r === "loss" ? "win" : "draw";
}

/** Round-robin déterministe de la semaine pour toute la poule.
   `myFrozen` : résultats FIGÉS de mes matchs (clé = round). Quand un round y
   figure, on utilise le résultat stocké au lieu de le recalculer — c'est ce qui
   garantit que tes matchs ne bougent plus une fois joués. Les matchs entre bots
   restent déterministes (leur état ne change pas). */
export function computeWeekTable(
  myPower: number,
  myName: string,
  weekStart: string,
  myFrozen?: Map<number, FixtureOutcome>,
  now: Date = new Date(),
  division = 1
): WeekTable {
  const N = MATCHES_PER_WEEK + 1; // 8 équipes → 7 journées
  const weekIndex = Math.floor(new Date(weekStart).getTime() / 604800000);
  const weekBase = division * 13 + weekIndex; // aligné sur generateWeekSchedule

  // Pairings round-robin (méthode du cercle), l'index 0 = moi, fixe.
  const positions = Array.from({ length: N }, (_, i) => i);
  const rounds: Array<Array<[number, number]>> = [];
  for (let r = 0; r < N - 1; r++) {
    const pairs: Array<[number, number]> = [];
    for (let i = 0; i < N / 2; i++) {
      pairs.push([positions[i], positions[N - 1 - i]]);
    }
    rounds.push(pairs);
    positions.splice(1, 0, positions.pop() as number); // rotation, index 0 fixe
  }

  // À quelle journée chaque bot m'affronte → on lui donne le seed weekBase+round
  // pour rester aligné sur le calendrier affiché (jour d → seed weekBase+d).
  const seedOf: Array<number | null> = new Array(N).fill(null);
  rounds.forEach((pairs, r) => {
    for (const [a, b] of pairs) {
      if (a === 0) seedOf[b] = weekBase + r;
      else if (b === 0) seedOf[a] = weekBase + r;
    }
  });

  // Métadonnées + bilans de chaque équipe.
  const rec: TeamStanding[] = Array.from({ length: N }, (_, i) => {
    if (i === 0) {
      return {
        name: myName,
        seed: null,
        power: myPower,
        isMe: true,
        played: 0, wins: 0, draws: 0, losses: 0, points: 0,
        goalsFor: 0, goalsAgainst: 0, fixtures: [],
      };
    }
    const seed = seedOf[i] as number;
    const bt = generateBotTeam(division, seed);
    return {
      name: bt.name,
      seed,
      power: bt.members.reduce((s, m) => s + m.power, 0),
      isMe: false,
      played: 0, wins: 0, draws: 0, losses: 0, points: 0,
      goalsFor: 0, goalsAgainst: 0, fixtures: [],
    };
  });

  const applyTo = (
    t: TeamStanding,
    res: "win" | "draw" | "loss" | null,
    gf: number | null,
    ga: number | null,
    f: TeamFixture
  ) => {
    t.fixtures.push(f);
    if (res === null) return;
    t.played += 1;
    t.goalsFor += gf ?? 0;
    t.goalsAgainst += ga ?? 0;
    if (res === "win") { t.wins += 1; t.points += 3; }
    else if (res === "draw") { t.draws += 1; t.points += 1; }
    else { t.losses += 1; }
  };

  rounds.forEach((pairs, r) => {
    const at = new Date(weekStart);
    at.setDate(at.getDate() + r);
    at.setHours(LEAGUE_WEEK_RESET_HOUR, 0, 0, 0);
    const scheduledAt = at.toISOString();
    const due = now.getTime() >= at.getTime();

    for (const [pa, pb] of pairs) {
      const lo = Math.min(pa, pb);
      const hi = Math.max(pa, pb);
      const A = rec[lo];
      const B = rec[hi];
      const mseed = (weekBase * 131071 + r * 8191 + lo * 251 + hi) | 0;
      // lo === 0 ⇒ c'est MON match (je suis l'index 0) : on prend le résultat
      // figé s'il existe, sinon on simule de façon déterministe.
      const o: FixtureOutcome | null = !due
        ? null
        : lo === 0 && myFrozen && myFrozen.has(r)
        ? (myFrozen.get(r) as FixtureOutcome)
        : simulateFixtureOutcome(A.power, B.power, mseed);

      applyTo(
        A,
        o ? o.result : null,
        o ? o.homeScore : null,
        o ? o.awayScore : null,
        {
          round: r, scheduledAt,
          opponentName: B.name, opponentSeed: B.seed, opponentIsMe: B.isMe,
          result: o ? o.result : null,
          goalsFor: o ? o.homeScore : null,
          goalsAgainst: o ? o.awayScore : null,
          due,
        }
      );
      applyTo(
        B,
        o ? mirrorResult(o.result) : null,
        o ? o.awayScore : null,
        o ? o.homeScore : null,
        {
          round: r, scheduledAt,
          opponentName: A.name, opponentSeed: A.seed, opponentIsMe: A.isMe,
          result: o ? mirrorResult(o.result) : null,
          goalsFor: o ? o.awayScore : null,
          goalsAgainst: o ? o.homeScore : null,
          due,
        }
      );
    }
  });

  const standings = [...rec].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const gdA = a.goalsFor - a.goalsAgainst;
    const gdB = b.goalsFor - b.goalsAgainst;
    if (gdB !== gdA) return gdB - gdA;
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
    return a.name.localeCompare(b.name);
  });

  const me = rec[0];
  return {
    standings,
    myRank: standings.findIndex((t) => t.isMe) + 1,
    matchesDone: me.played,
  };
}

export const GK_UPGRADE_COST = 100;

export function computeGoalkeeperLevel(
  members: Array<{ goalkeeper_contribution?: number }>
): number {
  const totalContrib = members.reduce(
    (s, m) => s + (m.goalkeeper_contribution ?? 0),
    0
  );
  return Math.max(1, Math.floor(totalContrib / GK_UPGRADE_COST) + 1);
}
